import React from "react";
import { withStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import { withTheme } from "@material-ui/core/styles";
import { Chart } from "react-google-charts";
import CircularProgress from "@material-ui/core/CircularProgress";
import AlbumAccordion from "./AlbumAccordion";

import Typography from "@material-ui/core/Typography";
import { Grid } from "@material-ui/core";
import {
  getTagsFromBackendAlbum,
  getTrackRatingFromBackendAlbum,
  makeBackendAPIRequest,
  linkTracks,
  backendBaseURL,
} from "../api_utilities/backend";
import { makeSpotifyAPIRequest } from "../api_utilities/spotify";
import { Divider } from "@material-ui/core";

const useStyles = (theme) => ({
  root: {
    padding: theme.spacing(2),
    height: "100%",
  },
  spaceBelow: {
    marginBottom: theme.spacing(2),
  },
  spaceAround: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
});

class MyRatings extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      ratingBreakdown: null,
      albumIDsByRating: null,
      ratingIsLoaded: Object.fromEntries(
        Array.from({ length: 10 }, (_, i) => [0.5 + i * 0.5, false])
      ),
      countTotalAlbums: null,
      selectedRating: null,
      albums: [],
      loading: true,
    };
  }

  componentDidMount() {
    const url = new URL(backendBaseURL + "/album/get_all_rated_albums");

    makeBackendAPIRequest({
      url: url,
      method: "GET",
      onSuccessCallback: (backendResponse) => {
        this.setState({
          loading: false,
          ratingBreakdown: backendResponse.ratingBreakdown,
          albumIDsByRating: backendResponse.albumIDsByRating,
          countTotalAlbums: Object.values(
            backendResponse.ratingBreakdown
          ).reduce((sum, n) => sum + n, 0),
        });
      },
      onFailureCallback: () => {},
    });
  }

  onAlbumOpen = (albumID) => {
    const url = new URL("https://api.spotify.com/v1/albums/" + albumID);
    makeSpotifyAPIRequest({
      url: url,
      method: "GET",
      onSuccessCallback: (data) => {
        const { albums } = this.state;

        // TODO: Store a map in state so we're not iterating every album
        for (const album of albums) {
          if (album.albumID === albumID) {
            const link_objects = [];

            album.tracks = data.tracks.items.map((track, i) => {
              const { rating, rating_links } = getTrackRatingFromBackendAlbum(
                track,
                album
              );
              const { tags, tag_links } = getTagsFromBackendAlbum(track, album);
              link_objects.push(...rating_links, ...tag_links);

              return {
                index: i,
                id: track.id,
                name: track.name,
                artistName: track.artists
                  .map((artist) => artist.name)
                  .join(", "),
                trackLength: track.duration_ms,
                trackNumber: track.track_number,
                rating: rating,
                tags: tags,
              };
            });
            album.hasLoadedTracks = true;
            this.setState({ albums });
            linkTracks(link_objects);

            return;
          }
        }
      },
      onNoContentCallback: () => {},
    });
  };

  onTrackRatedCallback = (albumID, albumIndex) => {
    const { albums } = this.state;

    const url = new URL(backendBaseURL + "/album/get_album");
    url.searchParams.append("id_spotify", albumID);

    makeBackendAPIRequest({
      url: url,
      method: "GET",
      onSuccessCallback: (backendAlbum) => {
        const album = albums[albumIndex];
        album.albumTotalTracksRated =
          backendAlbum.songs != null ? backendAlbum.songs.length : 0;
        album.albumRating = backendAlbum.rating;
        album.averageScore = backendAlbum.average_score;
        this.setState({ albums: albums });
      },
      onFailureCallback: () => {},
    });
  };

  onTrackRatedOptimistic = (albumIndex, trackIndex, newValue) => {
    const { albums } = this.state;
    albums[albumIndex].tracks[trackIndex].rating = newValue;
    this.setState({ albums: albums });
  };

  onTagsUpdatedOptimistic = (albumIndex, trackIndex, newValue) => {
    const { albums } = this.state;
    albums[albumIndex].tracks[trackIndex].tags = newValue;
    this.setState({ albums: albums });
  };

  onRatingClicked = ({ chartWrapper }) => {
    const { albumIDsByRating, albums, ratingIsLoaded } = this.state;
    const chart = chartWrapper.getChart();
    const selection = chart.getSelection();
    if (selection.length === 1) {
      const [selectedItem] = selection;
      const { row } = selectedItem;
      const selectedRating = 5 - row * 0.5;
      if (ratingIsLoaded[selectedRating]) {
        this.setState({ selectedRating });

        return;
      }

      const numLoadedAlbums = albums.length;

      const url = new URL(backendBaseURL + "/album/get_albums_by_true_id");

      makeBackendAPIRequest({
        url: url,
        method: "POST",
        body: { true_album_ids: albumIDsByRating[selectedRating.toFixed(1)] },
        onSuccessCallback: (backendResponse) => {
          const newAlbums = backendResponse.albums
            .sort((a, b) => {
              return (
                b.average_score - a.average_score ||
                a.album_infos[0].name
                  .toLowerCase()
                  .localeCompare(b.album_infos[0].name.toLowerCase())
              );
            })
            .map((album, i) => {
              const album_info = album.album_infos[0];
              return {
                albumIndex: i + numLoadedAlbums,
                albumID: album.id_spotify,
                albumName: album_info.name,
                albumArtists: album_info.artists,
                albumArtURL: album_info.art_url,
                releaseYear: album_info.release_year,
                albumTotalTracks: album.num_songs,
                albumTotalTracksRated:
                  album.songs != null ? album.songs.length : 0,
                albumRating: album.rating,
                averageScore: album.average_score,
                tracks: album.songs,
                tags: album.tags,
                hasLoadedTracks: false,
              };
            });

          ratingIsLoaded[selectedRating] = true;

          this.setState((prev) => ({
            albums: [...prev.albums, ...newAlbums],
            ratingIsLoaded,
            selectedRating,
          }));
        },
        onFailureCallback: () => {},
      });
    }
  };

  render() {
    const { classes, theme, store } = this.props;
    const {
      countTotalAlbums,
      selectedRating,
      loading,
      ratingBreakdown,
      albums,
    } = this.state;

    const albumsForRating = albums.filter(
      (album) => album.albumRating == selectedRating
    );

    return (
      <Card className={classes.root}>
        <Grid container>
          <Grid item container xs={12} justify={"flex-end"}>
            <Typography variant="overline">
              Rating Breakdown ({countTotalAlbums} rating
              {countTotalAlbums !== 1 ? "s" : ""})
            </Typography>
          </Grid>
          {loading ? (
            <Grid item container xs={12} justify={"center"}>
              <CircularProgress />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Chart
                width={"100%"}
                height={"500px"}
                chartType="BarChart"
                loader={<CircularProgress />}
                data={[
                  ["Rating", "Count"],
                  ...[
                    "5.0",
                    "4.5",
                    "4.0",
                    "3.5",
                    "3.0",
                    "2.5",
                    "2.0",
                    "1.5",
                    "1.0",
                    "0.5",
                  ].map((r) => [r, ratingBreakdown[Number(r).toFixed(1)] ?? 0]),
                ]}
                chartEvents={[
                  {
                    eventName: "select",
                    callback: this.onRatingClicked,
                  },
                ]}
                options={{
                  colors: [theme.palette.primary.main],
                  chartArea: {
                    width: "80%",
                    height: "80%",
                  },
                  legend: "none",
                  backgroundColor: {
                    fill: theme.palette.background.paper,
                  },
                  hAxis: {
                    gridlines: {
                      color: "transparent",
                    },
                    textStyle: {
                      color: theme.palette.primary.dark,
                    },
                    format: "#",
                  },
                  vAxis: {
                    textStyle: {
                      color: theme.palette.primary.light,
                      fontSize: 16,
                    },
                  },
                  bars: "horizontal",
                }}
              />
            </Grid>
          )}
          {selectedRating !== null && (
            <>
              <Grid item xs={12}>
                <Divider
                  variant={"fullWidth"}
                  className={classes.spaceAround}
                />
              </Grid>
              <Grid item container xs={12} justify={"flex-end"}>
                <Typography variant="overline">
                  Albums Rated {selectedRating}*
                </Typography>
              </Grid>
              {albumsForRating.length > 0 ? (
                albumsForRating.map((album) => (
                  <Grid item xs={12} key={album.albumID}>
                    <AlbumAccordion
                      trackAndAlbumInfo={album}
                      hasLoadedTracks={album.hasLoadedTracks}
                      onTrackRated={{
                        callback: this.onTrackRatedCallback,
                        optimistic: this.onTrackRatedOptimistic,
                      }}
                      onTagsUpdatedOptimistic={this.onTagsUpdatedOptimistic}
                      onOpen={this.onAlbumOpen}
                      store={store}
                    />
                  </Grid>
                ))
              ) : (
                <Grid item container justify={"center"} xs={12}>
                  <Typography variant="overline">No albums found</Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Card>
    );
  }
}

export default withStyles(useStyles)(withTheme(MyRatings));
